<template>
  <v-dialog :model-value="modelValue" max-width="1100" @update:model-value="close">
    <BaseDialogCard :title="tkey('microspaceOptimisation.title')" @close="close">
      <v-progress-linear v-if="loading || !isMatrixReady" indeterminate color="primary" />

      <template v-else>
        <v-alert v-if="errorMessage" type="error" variant="tonal" class="mb-4">
          {{ errorMessage }}
        </v-alert>

        <div class="allocation-matrix">
          <table>
            <thead>
              <tr>
                <th>{{ tkey('microspaceOptimisation.spacebreak') }}</th>
                <th v-for="cannGroup in leafCannGroups" :key="cannGroup.key">
                  {{ cannGroup.name }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="spacebreak in orderedSpacebreaks" :key="spacebreak._id">
                <th>
                  <div>{{ spacebreak.name }}</div>
                  <small>{{
                    tkey('microspaceOptimisation.capacity', [spacebreak[allocationSizeType]])
                  }}</small>
                </th>
                <td v-for="cannGroup in leafCannGroups" :key="cannGroup.key">
                  <v-text-field
                    v-model.number="targets[cannGroup.key][spacebreak._id]"
                    density="compact"
                    hide-details
                    min="0"
                    type="number"
                    variant="outlined"
                    @update:model-value="clearError"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <v-alert v-if="!isMatrixValid" type="warning" variant="tonal" class="mt-4">
          {{ tkey('microspaceOptimisation.validation') }}
        </v-alert>
      </template>

      <template #footer>
        <v-row>
          <v-col class="d-flex justify-space-between">
            <v-btn
              :disabled="loading || !hasSavedAllocation"
              color="error"
              variant="text"
              @click="deleteAllocation"
            >
              {{ tkey('microspaceOptimisation.delete') }}
            </v-btn>
            <div>
              <v-btn class="mr-2" :disabled="loading" @click="close">
                {{ $t('actions.cancel') }}
              </v-btn>
              <v-btn :disabled="loading || !isMatrixValid" @click="save">
                {{ $t('actions.save') }}
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </template>
    </BaseDialogCard>
  </v-dialog>
</template>

<script>
import axios from 'axios';
import to from 'await-to-js';
import { has, sortBy } from 'lodash';
import { useInternationalization } from '@/composables/use-internationalization';
import { useSnackbarStore } from '@/stores/snackbar';
import cannGroupUtils from '@/utils/cann-groups';
import checkpointTypes from '@enums/checkpoint-types';

const localizationKey = 'scenarioAssortmentPanel';

export default {
  props: {
    modelValue: {
      type: Boolean,
      required: true,
    },
    canvas: {
      type: Object,
      required: true,
    },
    cannGroups: {
      type: Array,
      required: true,
    },
    spacebreaks: {
      type: Array,
      required: true,
    },
    allocationSizeType: {
      type: String,
      default: 'productCountSize',
    },
  },

  emits: ['update:modelValue', 'saved'],

  setup() {
    const { tkey } = useInternationalization(localizationKey);
    const snackbarStore = useSnackbarStore();
    return { tkey, snackbarStore };
  },

  data() {
    return {
      loading: false,
      errorMessage: '',
      hasSavedAllocation: false,
      targets: {},
    };
  },

  computed: {
    leafCannGroups() {
      return cannGroupUtils.getLeafNodes(this.cannGroups);
    },

    orderedSpacebreaks() {
      return sortBy(this.spacebreaks, this.allocationSizeType);
    },

    columnTotals() {
      return this.orderedSpacebreaks.reduce((totals, spacebreak) => {
        totals[spacebreak._id] = this.leafCannGroups.reduce(
          (total, cannGroup) => total + Number(this.targets[cannGroup.key]?.[spacebreak._id] || 0),
          0
        );
        return totals;
      }, {});
    },

    isMatrixReady() {
      return this.leafCannGroups.every(cannGroup =>
        this.orderedSpacebreaks.every(spacebreak =>
          has(this.targets[cannGroup.key] || {}, spacebreak._id)
        )
      );
    },

    isMatrixValid() {
      if (!this.isMatrixReady) return false;

      return this.leafCannGroups.every(cannGroup => {
        let previousSpacebreakTarget = -1;
        return this.orderedSpacebreaks.every(spacebreak => {
          const targetProductCount = this.targets[cannGroup.key]?.[spacebreak._id];
          const spacebreakCapacity = spacebreak[this.allocationSizeType];
          if (
            !Number.isInteger(targetProductCount) ||
            targetProductCount < previousSpacebreakTarget ||
            targetProductCount < 0
          )
            return false;
          previousSpacebreakTarget = targetProductCount;
          return this.columnTotals[spacebreak._id] <= spacebreakCapacity;
        });
      });
    },
  },

  watch: {
    modelValue: {
      handler(isOpen) {
        if (isOpen) this.load();
      },
      immediate: true,
    },
  },

  methods: {
    clearError() {
      this.errorMessage = '';
    },

    createEmptyTargets() {
      this.targets = this.leafCannGroups.reduce((targets, cannGroup) => {
        targets[cannGroup.key] = this.orderedSpacebreaks.reduce((spacebreakTargets, spacebreak) => {
          spacebreakTargets[spacebreak._id] = 0;
          return spacebreakTargets;
        }, {});
        return targets;
      }, {});
    },

    applySavedMicrospaceOptimisation(microspaceOptimisation) {
      microspaceOptimisation.cannGroupAllocations.forEach(cannGroupAllocation => {
        cannGroupAllocation.spacebreakTargets.forEach(target => {
          if (this.targets[cannGroupAllocation.cannGroupId]?.[target.spacebreakId] !== undefined) {
            this.targets[cannGroupAllocation.cannGroupId][target.spacebreakId] =
              target.targetProductCount;
          }
        });
      });
    },

    applyReferenceAllocation(referenceProducts) {
      const spacebreakIndexes = new Map(
        this.orderedSpacebreaks.map((spacebreak, index) => [String(spacebreak._id), index])
      );

      referenceProducts.forEach(product => {
        const cannGroupTargets = this.targets[product.cannGroupId];
        const currentSpacebreakIndex = spacebreakIndexes.get(String(product.currentSpacebreakId));
        if (!cannGroupTargets || currentSpacebreakIndex === undefined) return;

        this.orderedSpacebreaks.slice(currentSpacebreakIndex).forEach(spacebreak => {
          cannGroupTargets[spacebreak._id] += 1;
        });
      });
    },

    async load() {
      this.loading = true;
      this.errorMessage = '';
      this.createEmptyTargets();
      const [canvasErr, canvasResponse] = await to(
        axios.get(`/api/assortment-canvases/${this.canvas._id}`)
      );
      if (canvasErr) {
        this.errorMessage = canvasErr.message;
        this.loading = false;
        return;
      }

      const microspaceOptimisation = canvasResponse.data.microspaceOptimisation;
      this.hasSavedAllocation = !!microspaceOptimisation;
      if (microspaceOptimisation) {
        this.applySavedMicrospaceOptimisation(microspaceOptimisation);
        this.loading = false;
        return;
      }

      const [checkpointErr, checkpointResponse] = await to(
        axios.get(`/api/assortment-canvases/${this.canvas._id}/checkpoints`, {
          params: {
            where: {
              scenarioId: this.canvas.scenarioId,
              storeClassId: this.canvas.storeClassId,
              'checkpointMeta.type': checkpointTypes.previousAssortment,
            },
          },
        })
      );
      if (checkpointErr) {
        this.errorMessage = checkpointErr.message;
        this.loading = false;
        return;
      }

      const previousAssortmentCheckpoint = checkpointResponse.data[0];
      if (previousAssortmentCheckpoint) {
        const [productsErr, productsResponse] = await to(
          axios.get(
            `/api/assortment-canvas-products/scenario/${this.canvas.scenarioId}/canvas/${previousAssortmentCheckpoint._id}/all`
          )
        );
        if (productsErr) this.errorMessage = productsErr.message;
        else this.applyReferenceAllocation(productsResponse.data);
      }
      this.loading = false;
    },

    createPayload() {
      return {
        allocationSizeType: this.allocationSizeType,
        cannGroupAllocations: this.leafCannGroups.map(cannGroup => ({
          cannGroupId: cannGroup.key,
          spacebreakTargets: this.orderedSpacebreaks.map(spacebreak => ({
            spacebreakId: spacebreak._id,
            targetProductCount: this.targets[cannGroup.key][spacebreak._id],
          })),
        })),
      };
    },

    async save() {
      this.loading = true;
      this.errorMessage = '';
      const [err] = await to(
        axios.post(`/api/assortment-canvases/${this.canvas._id}/microspace-optimisation`, {
          microspaceOptimisation: this.createPayload(),
        })
      );
      this.loading = false;
      if (err) {
        this.errorMessage = err.message;
        return;
      }
      this.snackbarStore.showSuccess(this.$t('actions.saveSuccess'));
      this.hasSavedAllocation = true;
      this.$emit('saved');
      this.close();
    },

    async deleteAllocation() {
      this.loading = true;
      this.errorMessage = '';
      const [err] = await to(
        axios.delete(`/api/assortment-canvases/${this.canvas._id}/microspace-optimisation`)
      );
      this.loading = false;
      if (err) {
        this.errorMessage = err.message;
        return;
      }
      this.snackbarStore.showSuccess(this.$t('actions.deleteSuccess'));
      this.$emit('saved');
      this.close();
    },

    close() {
      this.$emit('update:modelValue', false);
    },
  },
};
</script>

<style scoped>
.allocation-matrix {
  max-width: 100%;
  overflow-x: auto;
}

table {
  border-collapse: collapse;
  width: 100%;
}

th,
td {
  border: 1px solid rgb(var(--v-theme-outline-variant));
  padding: 8px;
  text-align: center;
  vertical-align: middle;
}

th:first-child {
  min-width: 180px;
  text-align: left;
}

td {
  min-width: 112px;
}
</style>
